import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useDeviceConfigurationCache } from '../configuration/useDeviceConfigurationCache';
import { getPersonalListType } from '../configuration/utils';
import { createMissingInitRhinoError } from '../errors';
import { useRefetchQuery } from '../hooks/useRefetchQuery';
import { queryKeys } from '../queryKeys';

import { createMissingPersonalListTypeError } from './errors';
import { createPersonalLineupQueryOptions } from './queryOptions';

type UsePersonalLineupProps = {
  enabled?: boolean;
  staleTime?: number;
};

export function usePersonalLineup({ enabled = true, staleTime }: UsePersonalLineupProps) {
  const { configuration } = useDeviceConfigurationCache();

  const listType = configuration ? getPersonalListType(configuration) : undefined;

  const {
    data,
    error,
    isLoading,
    isFetching,
    refetch: refetchPersonalLineup,
  } = useQuery({
    ...(listType && enabled
      ? createPersonalLineupQueryOptions({ listType, staleTime })
      : { queryKey: queryKeys.disabled }),
    enabled: Boolean(listType) && enabled,
  });

  const configurationError = useMemo(() => {
    if (!enabled) {
      return;
    }

    if (!configuration) {
      return createMissingInitRhinoError();
    }

    if (!listType) {
      return createMissingPersonalListTypeError();
    }
  }, [configuration, enabled, listType]);

  const personalLineupError = configurationError ?? error;

  const personalLineup = useMemo(() => {
    if (personalLineupError) {
      return undefined;
    }

    if (!data) {
      return undefined;
    }

    if (data.allLineups.length) {
      return data;
    }
  }, [data, personalLineupError]);

  const refetch = useRefetchQuery({ enabled: Boolean(listType && enabled), refetch: refetchPersonalLineup });

  return {
    error: personalLineupError,
    isLoading,
    isFetching,
    personalLineup,
    refetch,
  };
}
